import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { CONFIRM_RESULT, ConfirmationDialogComponent, ConfirmOptions, RMS_DIALOG_SM_CONFIG } from '@rms/sdk/dialog';
import { map, Observable } from 'rxjs';

import { DEFAULT_SNACKBAR_CONFIG, ERROR_SNACKBAR_CONFIG } from './notification.models';

/**
 * Wrapper around MatSnackBar
 */

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);

  public notify(messageToken: string, actionToken: string, config?: MatSnackBarConfig, onAction?: () => void): void {
    this.translate(messageToken, actionToken).subscribe(([message, action]) => {
      const snackBarRef = this.snackBar.open(message, action, config ? config : DEFAULT_SNACKBAR_CONFIG);

      if (onAction) {
        snackBarRef.onAction().subscribe(() => onAction());
      }
    });
  }

  public error(messageToken: string, actionToken = 'common.ok', config?: MatSnackBarConfig): void {
    this.translate(messageToken, actionToken).subscribe(([message, action]) => {
      this.snackBar.open(message, action, config ? config : ERROR_SNACKBAR_CONFIG);
    });
  }

  public confirm(options: ConfirmOptions): Observable<CONFIRM_RESULT> {
    const dialog = this.dialog.open(ConfirmationDialogComponent, {
      ...RMS_DIALOG_SM_CONFIG,
      panelClass: [...(RMS_DIALOG_SM_CONFIG.panelClass as string[]), 'rms-dialog-confirm'],
      data: options,
    });

    return dialog.afterClosed();
  }

  private translate(messageToken: string, actionToken: string): Observable<[string, string]> {
    return this.translateService
      .get([messageToken, actionToken])
      .pipe(map((data): [string, string] => [String(data[messageToken]), String(data[actionToken])]));
  }
}
